// client/src/components/pages/LexiStar/Kpis/Kpis.tsx
//
// LexiStar KPIs (ST-00188 M4-D, T7-S02; LS-03 FR-RPT-001 / FR-RPT-006;
// m4-contracts §4, §8). Route /lexistar/kpis, page permission
// lexistar.kpis.view.
//
// Lane / route / truck / driver / run tabs, each a sortable table of the
// contract's metric set (trips, tonnes, empty-running %, cycle time,
// utilisation, fuel variance, on-route %, review share) for the period, with a
// trend sparkline of the sorted metric per row and drill-through to the trips
// behind each row. One GET /kpis/breakdown?series=true per column — each row
// carries its trend, so the sparklines cost no further requests; against a
// server that sends no `series` the trend per visible row comes from GET /kpis
// for that scope instead. The page formats; it computes nothing. Tab, period
// and sort live in the URL.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaTable, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { getKpiBreakdown, getKpis, getMetrics } from '../../../../services/lexistarAnalytics';
import type { BreakdownResponse, KpisResponse, MetricsCatalogueResponse } from '../../../../types/lexistar-analytics';
import { NoPermissionPage } from '../StatusBoard/atoms';
import { PageHeader, SegmentedControl, useAuth } from '../../../../types/commonComponents';
import {
  PeriodPicker,
  ProvenanceLegend,
  ProvenanceMark,
  SkeletonBlock,
  Sparkline,
  StateChip,
  cardClasses,
  type SparkPoint,
} from '../Dashboard/analyticsAtoms';
import { formatValue } from '../Dashboard/metricFormat';
import { grainFor, periodLabel, readPeriod } from '../Dashboard/periodLogic';
import {
  type Breakdown,
  type MetricDef,
  normaliseBreakdown,
  normaliseCatalogue,
  normaliseKpis,
  seriesOf,
} from '../Dashboard/kpiModel';
import {
  TABS,
  type Column,
  type Row,
  type Sort,
  type TabId,
  columnsFor,
  inlineTrend,
  isTab,
  mapLimit,
  mergeBreakdowns,
  readSort,
  sortRows,
} from './kpiTable';

const PAGE = 25;
const TREND_CONCURRENCY = 6;

type ErrorLike = { response?: { status?: number; data?: { error?: string; message?: string } }; message?: string };
const errorText = (err: ErrorLike, fallback: string): string =>
  err?.response?.status === 403
    ? 'You do not have permission to read these figures.'
    : err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

/** Where a row goes when no metric gave it a drill link: the trips of that scope in the period. */
const SCOPE_PARAM: Record<TabId, string> = { lane: 'lane_id', route: 'route_id', asset: 'asset', driver: 'driver_id', run: 'run_id' };

export default function Kpis() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.kpis.view');
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: TabId = isTab(tabParam) ? tabParam : 'lane';
  const period = readPeriod(params);
  const periodKey = `${period.from}|${period.to}`;

  const update = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
          return next;
        },
        { replace: true }
      ),
    [setParams]
  );

  // ── Catalogue → columns ───────────────────────────────────────────────────
  const [catalogue, setCatalogue] = useState<Map<string, MetricDef> | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    getMetrics()
      .then((res: MetricsCatalogueResponse) => alive && setCatalogue(normaliseCatalogue(res)))
      .catch((err: ErrorLike) => alive && setCatalogueError(errorText(err, 'The metric catalogue could not be loaded.')));
    return () => {
      alive = false;
    };
  }, [canView]);
  const columns = useMemo(() => (catalogue ? columnsFor(tab, catalogue) : []), [catalogue, tab]);
  const columnKeys = columns.map((c) => c.key).join(',');
  const sort: Sort = readSort(params, columns);

  // ── One breakdown per column ──────────────────────────────────────────────
  const [breakdowns, setBreakdowns] = useState<Record<string, Breakdown | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [limit, setLimit] = useState(PAGE);
  useEffect(() => {
    if (!canView || !columns.length) return undefined;
    let alive = true;
    setLoading(true);
    setErrors([]);
    setBreakdowns({});
    setLimit(PAGE);
    Promise.all(
      columns.map((c) =>
        getKpiBreakdown({ metric: c.key, by: tab, from: period.from, to: period.to, series: true })
          .then((res: BreakdownResponse) => ({ key: c.key, b: normaliseBreakdown(res), error: null as string | null }))
          .catch((err: ErrorLike) => ({ key: c.key, b: undefined, error: `${c.label}: ${errorText(err, 'could not be loaded')}` }))
      )
    ).then((results) => {
      if (!alive) return;
      const next: Record<string, Breakdown | undefined> = {};
      results.forEach((r) => {
        next[r.key] = r.b;
      });
      setBreakdowns(next);
      setErrors(results.map((r) => r.error).filter((e): e is string => Boolean(e)));
      setLoading(false);
    });
    return () => {
      alive = false;
    };
    // columnKeys / periodKey carry the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, tab, columnKeys, periodKey]);

  const rows = useMemo(() => sortRows(mergeBreakdowns(breakdowns, columns), sort), [breakdowns, columns, sort.key, sort.dir]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = rows.slice(0, limit);
  const trendKey = sort.key !== 'label' ? sort.key : (columns[0]?.key ?? null);
  const trendColumn = columns.find((c) => c.key === trendKey) || null;

  // ── Trend per visible row ─────────────────────────────────────────────────
  // From the trend column's breakdown series; without them (an older server)
  // one GET /kpis per visible row for that scope.
  const trendBreakdown = trendKey ? breakdowns[trendKey] : undefined;
  const fetchTrends = !loading && !trendBreakdown?.hasSeries;
  const grain = grainFor(period);
  const [trends, setTrends] = useState<Record<string, SparkPoint[]>>({});
  const trendId = (id: string) => `${tab}|${periodKey}|${trendKey}|${id}`;
  const shownIds = shown.map((r) => r.id).join('|');
  useEffect(() => {
    if (!canView || !trendKey || !shown.length || !fetchTrends) return undefined;
    let alive = true;
    const want = shown.filter((r) => !trends[trendId(r.id)]);
    if (!want.length) return undefined;
    mapLimit(want, TREND_CONCURRENCY, (r) =>
      getKpis({ scope_type: tab, scope_id: r.id, grain, from: period.from, to: period.to, metrics: trendKey })
        .then((res: KpisResponse) => [r.id, seriesOf(normaliseKpis(res), trendKey)] as const)
        .catch(() => [r.id, [] as SparkPoint[]] as const)
    ).then((pairs) => {
      if (!alive) return;
      setTrends((t) => {
        const next = { ...t };
        pairs.forEach(([id, pts]) => {
          next[trendId(id)] = pts;
        });
        return next;
      });
    });
    return () => {
      alive = false;
    };
    // shownIds carries the visible rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, trendKey, shownIds, tab, periodKey, grain, fetchTrends]);

  // ── Render ────────────────────────────────────────────────────────────────
  const header = (
    <PageHeader
      icon={FaTable}
      title="KPIs"
      subtitle="Lane, route, truck, driver and run figures for the period — sort, compare, open the trips behind a row."
      toolbar={canView ? <PeriodPicker period={period} onChange={(p) => update({ from: p.from, to: p.to })} /> : null}
      inline={false}
    />
  );
  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view KPIs." />;

  const noun = TABS.find((t) => t.id === tab)?.noun ?? 'Scope';
  const setSort = (key: string) => {
    const dir = sort.key === key ? (sort.dir === 'desc' ? 'asc' : 'desc') : key === 'label' ? 'asc' : 'desc';
    update({ sort: key, dir });
  };
  const sortIcon = (key: string) =>
    sort.key !== key ? (
      <FaSort className="inline ml-1 text-slate-300" size={9} aria-hidden="true" />
    ) : sort.dir === 'asc' ? (
      <FaSortUp className="inline ml-1 text-slate-600" size={9} aria-hidden="true" />
    ) : (
      <FaSortDown className="inline ml-1 text-slate-600" size={9} aria-hidden="true" />
    );
  const ariaSort = (key: string): 'ascending' | 'descending' | 'none' =>
    sort.key === key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  const rowDrill = (r: Row): string =>
    r.drill || `/lexistar/trips?${new URLSearchParams({ from: period.from, to: period.to, [SCOPE_PARAM[tab]]: r.id })}`;
  const provisionalRows = rows.filter((r) => r.state === 'provisional').length;

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SegmentedControl<TabId>
            ariaLabel="KPI scope"
            value={tab}
            onChange={(t) => update({ tab: t, sort: null, dir: null })}
            options={TABS.map((t) => ({ value: t.id, label: t.label }))}
          />
          <p className="text-xs text-slate-600 tabular-nums" role="status" aria-live="polite">
            {periodLabel(period)}
            {!loading && ` · ${rows.length} ${noun.toLowerCase()}${rows.length === 1 ? '' : 's'}`}
            {provisionalRows > 0 && ` · ${provisionalRows} with provisional figures`}
          </p>
        </div>

        {tab === 'driver' && (
          <p className="text-xs text-slate-600 bg-surface border border-slate-200 rounded-lg px-3 py-2">
            Driver figures are for coaching: they describe the trips a driver ran, on the routes and trucks they were given. Look at
            trends, not single trips.
          </p>
        )}

        {(catalogueError || errors.length > 0) && (
          <div role="alert" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {catalogueError || `Some columns could not be loaded — ${errors.join('; ')}`}
          </div>
        )}

        <section className={`${cardClasses} overflow-hidden`} aria-label={`${noun} KPIs`}>
          {!catalogue && !catalogueError ? (
            <div className="p-4 space-y-2" aria-busy="true">
              {Array.from({ length: 6 }, (_, i) => (
                <SkeletonBlock key={i} className="h-9" />
              ))}
            </div>
          ) : catalogue && !columns.length ? (
            <div className="px-4 py-10 text-center text-sm text-slate-600">No KPIs are kept for this scope, or you cannot read them.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th scope="col" aria-sort={ariaSort('label')} className="sticky left-0 z-[1] bg-slate-50 px-3 py-2.5 text-left">
                      <button
                        type="button"
                        onClick={() => setSort('label')}
                        className="text-xs font-semibold text-slate-700 uppercase tracking-wide whitespace-nowrap"
                      >
                        {noun}
                        {sortIcon('label')}
                      </button>
                    </th>
                    {columns.map((c: Column) => (
                      <th key={c.key} scope="col" aria-sort={ariaSort(c.key)} className="px-3 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => setSort(c.key)}
                          title={`${c.def.name} — ${c.def.formula}`}
                          className="text-xs font-semibold text-slate-700 uppercase tracking-wide whitespace-nowrap"
                        >
                          {c.short}
                          {sortIcon(c.key)}
                        </button>
                      </th>
                    ))}
                    <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold text-slate-700 uppercase tracking-wide whitespace-nowrap">
                      Trend{trendColumn ? ` · ${trendColumn.short}` : ''}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading ? (
                    Array.from({ length: 6 }, (_, i) => (
                      <tr key={i}>
                        <td colSpan={columns.length + 2} className="px-3 py-2">
                          <SkeletonBlock className="h-6" />
                        </td>
                      </tr>
                    ))
                  ) : !rows.length ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="px-3 py-10 text-center text-sm text-slate-600">
                        No {noun.toLowerCase()} recorded trips in this period.
                      </td>
                    </tr>
                  ) : (
                    shown.map((r) => {
                      const trend = trendKey
                        ? (inlineTrend(r, trendKey, trendBreakdown) ?? trends[trendId(r.id)])
                        : undefined;
                      return (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <th scope="row" className="sticky left-0 z-[1] bg-surface px-3 py-2 text-left font-normal max-w-[16rem]">
                            <Link to={rowDrill(r)} className="font-medium text-slate-900 hover:underline truncate block" title={`Open the trips behind ${r.label}`}>
                              {r.label}
                            </Link>
                            {r.state === 'provisional' && <StateChip state="provisional" className="mt-0.5" />}
                          </th>
                          {columns.map((c) => {
                            const cell = r.cells[c.key];
                            return (
                              <td key={c.key} className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-800">
                                {cell && cell.value !== null ? (
                                  <>
                                    {formatValue(cell.value, cell.unit ?? c.def.unit)}
                                    <ProvenanceMark provenance={cell.provenance} className="ml-1" />
                                  </>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="px-3 py-2">
                            {trend ? (
                              <Sparkline points={trend} width={96} height={24} label={`${trendColumn?.label ?? 'Trend'} for ${r.label}`} />
                            ) : (
                              <SkeletonBlock className="h-5 w-24" />
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!loading && rows.length > shown.length && (
            <div className="border-t border-slate-100 px-3 py-2.5 text-center">
              <button
                type="button"
                onClick={() => setLimit((n) => n + PAGE)}
                className="text-sm font-medium text-primary-700 hover:underline"
              >
                Show {Math.min(PAGE, rows.length - shown.length)} more of {rows.length - shown.length}
              </button>
            </div>
          )}
        </section>
        <p className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1">
          <ProvenanceLegend />
          <span>Hover a column heading for how it is counted.</span>
        </p>
      </div>
    </div>
  );
}
